import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';
import { ConfigService } from '../general/config.service';
import { DictionaryCommonService } from '../general/dictionary-common.service';
import { ApplicationLinkCodeType } from './application-link-codes';

/**
 * Links to the Keez manual, read by code from the 'application_link' dictionary.
 * The list is loaded on the first request (not at app start) and kept in memory until the page is reloaded.
 */
@Injectable({ providedIn: 'root' })
export class ApplicationLinkService {
	// Keep the promise, so concurrent requests share a single API call.
	private links: Promise<Map<string, string>> = null;

	constructor(private dictionaryCommonService: DictionaryCommonService, private configService: ConfigService, private toastr: ToastrService) {
	}

	public async getUrl(code: ApplicationLinkCodeType): Promise<string> {
		const links = await this.loadLinks();
		return links.get(code) ?? null;
	}

	/** Opens the link in a new tab. Must be called directly from a click handler. */
	public open(code: ApplicationLinkCodeType): void {
		// Open the tab synchronously, while still in the click; opening it after the API call would be blocked as a popup.
		const win = window.open('', '_blank');
		if (win) {
			win.opener = null;
		}

		this.getUrl(code).then((url) => {
			if (url && (url.startsWith('https://') || url.startsWith('http://'))) {
				if (win) {
					win.location.href = url;
				}
				else {
					window.open(url, '_blank', 'noopener noreferrer');
				}
			}
			else {
				win?.close();
				this.showNotFound();
			}
		});
	}

	public showNotFound(): void {
		this.toastr.error('Link-ul către manual nu este disponibil momentan.');
	}

	private loadLinks(): Promise<Map<string, string>> {
		if (!this.links) {
			this.links = this.dictionaryCommonService.getDictionary(this.configService.accountsUrl, 'application_link').pipe(take(1)).toPromise()
				.then((result: any[]) => {
					if (!result) {
						// Failed call: don't keep the empty result, so the next request retries.
						this.links = null;
						return new Map<string, string>();
					}

					return new Map<string, string>(result.filter((l) => l?.code && l?.url).map((l) => [l.code, l.url]));
				});
		}

		return this.links;
	}
}
